import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Mail, Paperclip, X, AlertCircle } from "lucide-react";

/**
 * Reusable Gmail composer. Renders a dialog with To/CC/BCC/Subject/Body plus
 * an "attach current record's Excel export" shortcut. All emails route through
 * /api/gmail/send using the currently-signed-in user's connected Gmail.
 *
 * Props:
 *   open, onClose
 *   defaultTo, defaultSubject, defaultBody
 *   relatedModule, relatedRecordId, relatedRecordNumber
 *   attachmentEndpoint  (optional) — API path (relative) whose blob response is
 *                        offered as an attachment (e.g. `/quotations/{id}/export`)
 *   attachmentFilename  (optional)
 */
export default function SendEmailDialog({
  open, onClose,
  defaultTo = "", defaultSubject = "", defaultBody = "",
  relatedModule, relatedRecordId, relatedRecordNumber,
  attachmentEndpoint, attachmentFilename,
}) {
  const [form, setForm] = useState({ to: defaultTo, cc: "", bcc: "", subject: defaultSubject, body: defaultBody });
  const [attachIncluded, setAttachIncluded] = useState(!!attachmentEndpoint);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { configured, connected, email }

  useEffect(() => {
    if (!open) return;
    setForm({ to: defaultTo, cc: "", bcc: "", subject: defaultSubject, body: defaultBody });
    setAttachIncluded(!!attachmentEndpoint);
    (async () => {
      try {
        const [{ data: cfg }, { data: st }] = await Promise.all([
          api.get("/gmail/config-status"),
          api.get("/gmail/status"),
        ]);
        setStatus({ configured: cfg.configured, message: cfg.message, ...st });
      } catch {
        setStatus({ configured: false, connected: false, message: "Failed to load Gmail status" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function send() {
    const to = form.to.split(",").map((s) => s.trim()).filter(Boolean);
    if (to.length === 0) { toast.error("At least one 'To' address is required"); return; }
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    setSending(true);
    try {
      // Fetch attachment if requested
      const attachments = [];
      if (attachIncluded && attachmentEndpoint) {
        const token = localStorage.getItem("hispl_token");
        const API = process.env.REACT_APP_BACKEND_URL + "/api";
        const res = await fetch(API + attachmentEndpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error("Failed to fetch attachment for email");
        const blob = await res.blob();
        const dataBase64 = await blobToBase64(blob);
        attachments.push({
          filename: attachmentFilename || "attachment.xlsx",
          mime: blob.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          data_base64: dataBase64,
        });
      }

      await api.post("/gmail/send", {
        to,
        cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean),
        bcc: form.bcc.split(",").map((s) => s.trim()).filter(Boolean),
        subject: form.subject,
        body_text: form.body,
        body_html: form.body.split("\n").map((l) => `<p>${escapeHtml(l)}</p>`).join(""),
        attachments,
        related_module: relatedModule,
        related_record_id: relatedRecordId,
        related_record_number: relatedRecordNumber,
      });
      toast.success("Email sent");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Send Email</DialogTitle>
          <DialogDescription>
            Sent from your connected Gmail account
            {status?.email ? <> · <span className="font-mono text-xs">{status.email}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        {status && !status.configured && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Gmail is not configured on the server yet.</div>
              <div className="mt-1">Ask your Administrator to add <code className="font-mono">GOOGLE_CLIENT_ID</code>, <code className="font-mono">GOOGLE_CLIENT_SECRET</code> and <code className="font-mono">GMAIL_REDIRECT_URI</code> to backend/.env.</div>
            </div>
          </div>
        )}
        {status?.configured && !status?.connected && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>Your Gmail account isn't connected. Go to <a href="/profile" className="underline font-semibold">My Profile → Email Integration</a> to connect.</div>
          </div>
        )}

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-600">To *</Label>
            <Input data-testid="email-to" value={form.to}
                   onChange={(e) => setForm({ ...form, to: e.target.value })}
                   placeholder="customer@example.com (comma-separated for multiple)" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">CC</Label>
              <Input data-testid="email-cc" value={form.cc}
                     onChange={(e) => setForm({ ...form, cc: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">BCC</Label>
              <Input data-testid="email-bcc" value={form.bcc}
                     onChange={(e) => setForm({ ...form, bcc: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-600">Subject *</Label>
            <Input data-testid="email-subject" value={form.subject}
                   onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-600">Message</Label>
            <Textarea data-testid="email-body" value={form.body} rows={8}
                      onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          {attachmentEndpoint && (
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="attach-toggle">
              <input type="checkbox" checked={attachIncluded}
                     onChange={(e) => setAttachIncluded(e.target.checked)} />
              <Paperclip className="w-3.5 h-3.5" />
              Attach <span className="font-mono">{attachmentFilename || "the current document"}</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
          <Button onClick={send} disabled={sending || !status?.connected}
                  data-testid="email-send-btn"
                  className="bg-red-700 hover:bg-red-800 text-white">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[c]));
}
